/* ------------------------------------------------------------------ *
 * Funnel v3 — orchestrator (redesigned June 2026 — EU stores)
 *
 * Architecture (locked):
 *   Multipack PDP route:
 *     ATC → BOGSHP_PAIR popup (single binary decision, ~70% accepter rate)
 *     drawer → Checkout click branches on cart state:
 *       BOGSHP accepter + cart < threshold → FREE_SHIP_NUDGE popup
 *       BOGSHP accepter + cart ≥ threshold → /checkout
 *       BOGSHP rejecter → FC1 → FC2 → /checkout (sequential, independent)
 *
 *   Black PDP route:
 *     ATC → drawer (no popup) → /checkout (no popup)
 *
 *   Final popup (VIP) — schema toggle, default OFF.
 *
 * Cart-line properties stamped:
 *   BOGSHP_PAIR accept     → cart/change line qty=2  (R2 BOGSHP discount fires)
 *   FREE_SHIP_NUDGE accept → cart/add multipack +/or black at half price
 *                            (related=true on each added line → R3 reprice 50%).
 *                            R3 is the correct rule here (NOT R4 finalchance) —
 *                            BOGSHP-floor: FSN extras never drop below 50% off.
 *   FC1 accept             → cart/add multipack qty=1 + _offer=finalchance
 *   FC2 accept             → cart/add black qty=1 + _offer=finalchance
 *   VIP accept             → cart/add vip variant + _offer=finalchance + _vip=true
 *
 * Order-attribute stamps (Funnel: …) tracked per popup outcome with audit
 * at finishToCheckout for accepted-but-removed detection.
 * ------------------------------------------------------------------ */

(function () {
  'use strict';

  // ───── singleton guard ─────
  if (window.Funnel && window.Funnel._booted) return;
  window.Funnel = window.Funnel || {};
  window.Funnel._booted = true;

  // ───── config / constants ─────
  const STATES = Object.freeze({
    IDLE: 'idle',
    BOGSHP_PAIR: 'bogshp-pair',
    FREE_SHIP_NUDGE: 'free-ship-nudge',
    FC1: 'fc1',
    FC2: 'fc2',
    VIP: 'vip',
  });

  const SWAP_MS = 150;
  const DBG = !!window.Funnel.debug;
  const log = (...a) => { if (DBG) console.log('[funnel]', ...a); };

  // ───── product context (from PDP) ─────
  const product = window.Funnel.product || null;

  // ───── DOM refs ─────
  const modal = document.getElementById('funnel-modal');
  const body = modal ? modal.querySelector('[data-funnel-body]') : null;
  if (!modal || !body) log('modal element missing — orchestrator inert');

  // ───── money formatting ─────
  const moneyFormat = window.Funnel.moneyFormat || '€{{amount}}';
  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const amount = (cents / 100);
    const withDecimals = amount.toFixed(2);
    const withComma = withDecimals.replace('.', ',');
    return moneyFormat
      .replace('{{amount}}', withDecimals)
      .replace('{{amount_no_decimals}}', Math.round(amount).toString())
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(amount).toString());
  }

  // ───── cart helpers ─────
  async function cartAdd(variantId, qty, properties) {
    const body = { id: String(variantId), quantity: qty };
    if (properties && Object.keys(properties).length) body.properties = properties;
    log('cart/add', body);
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error('cart/add failed: ' + res.status + ' ' + text);
    }
    return res.json();
  }

  async function setVariantTotalQty(variantId, targetQty) {
    const cart = await fetchCart();
    const matches = cart.items.filter(i =>
      Number(i.variant_id) === Number(variantId) &&
      !(i.properties && String(i.properties._offer || '').toLowerCase() === 'finalchance')
    );
    if (matches.length === 0) return cartAdd(variantId, targetQty);
    const updates = {};
    matches.forEach((item, idx) => { updates[item.key] = idx === 0 ? targetQty : 0; });
    log('cart/update', updates);
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) throw new Error('cart/update failed: ' + res.status);
    return res.json();
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  // ───── Live cheapest shipping rate (Shopify zones) ─────
  // Queries /cart/shipping_rates.json with the merchant-configured default
  // country and returns the cheapest rate as cents. The FSN popup displays
  // it as the strike-through "was" price to keep the saving figure honest
  // as zone rates change.
  //
  // NOTE: country only — no ZIP. Sending shipping_address[zip] would cause
  // Shopify to persist that ZIP on the cart and pre-fill it at checkout.
  // All fleet stores use flat-rate-per-country shipping, so the ZIP adds
  // zero accuracy. (Prev: fsnDefaultZip setting — now dead, see playbook.)
  //
  // sessionStorage cached for 1h so the second+ FSN open in a session
  // doesn't re-hit the API. Returns null on any failure → caller falls
  // back to the static fsnShippingRateCents config value.
  const _SHIP_CACHE_KEY = 'funnel_cheapest_shipping_cents';
  const _SHIP_CACHE_AT_KEY = 'funnel_cheapest_shipping_at';
  const _SHIP_CACHE_TTL_MS = 60 * 60 * 1000;
  async function fetchCheapestShippingRateCents() {
    try {
      const cached = parseInt(sessionStorage.getItem(_SHIP_CACHE_KEY) || '', 10);
      const cachedAt = parseInt(sessionStorage.getItem(_SHIP_CACHE_AT_KEY) || '0', 10);
      if (Number.isFinite(cached) && cached > 0 && (epochNow() - cachedAt) < _SHIP_CACHE_TTL_MS) {
        return cached;
      }
    } catch (e) {}
    const cfg = window.Funnel.config || {};
    const country = String(cfg.fsnDefaultCountry || '').toUpperCase().trim();
    if (!country) return null; // not configured → fall back silently
    try {
      const params = new URLSearchParams();
      params.set('shipping_address[country]', country);
      const res = await fetch(`/cart/shipping_rates.json?${params.toString()}`, {
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const rates = (data && data.shipping_rates) || [];
      if (rates.length === 0) return null;
      let cheapestMajor = Infinity;
      for (const r of rates) {
        const p = parseFloat(r.price);
        if (Number.isFinite(p) && p < cheapestMajor) cheapestMajor = p;
      }
      if (!Number.isFinite(cheapestMajor)) return null;
      const cents = Math.round(cheapestMajor * 100);
      try {
        sessionStorage.setItem(_SHIP_CACHE_KEY, String(cents));
        sessionStorage.setItem(_SHIP_CACHE_AT_KEY, String(epochNow()));
      } catch (e) {}
      return cents;
    } catch (e) {
      return null;
    }
  }
  // Date.now() shim — wrapped so unit tests can stub time if needed.
  function epochNow() { return Date.now(); }

  function dispatchCartDrawerRefresh() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
  }
  function dispatchCartDrawerOpen() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
  }

  // ───── modal manager ─────
  let currentState = STATES.IDLE;
  let busy = false;

  function hydrate(content, data) {
    content.querySelectorAll('[data-slot]').forEach(el => {
      const key = el.dataset.slot;
      if (!(key in data)) return;
      const v = data[key];
      if (el.tagName === 'IMG') {
        el.setAttribute('src', v || '');
        if (data.imageAlt) el.setAttribute('alt', data.imageAlt);
      } else {
        el.textContent = v == null ? '' : v;
      }
    });
  }

  function resolveTemplateName(state) {
    return state + '-default';
  }

  function renderState(state, data) {
    const tplName = resolveTemplateName(state);
    let tpl = document.querySelector(`[data-funnel-template="${tplName}"]`);
    if (!tpl) tpl = document.querySelector(`[data-funnel-template="${state}"]`);
    if (!tpl) { log('no template for', tplName); return null; }
    const frag = tpl.content.cloneNode(true);
    const wrapper = document.createElement('div');
    wrapper.appendChild(frag);
    hydrate(wrapper, data || {});
    return wrapper;
  }

  async function setState(state, data) {
    if (!modal || !body) return;
    const incoming = renderState(state, data);
    if (!incoming) return;
    if (currentState === STATES.IDLE) {
      body.innerHTML = '';
      while (incoming.firstChild) body.appendChild(incoming.firstChild);
      currentState = state;
      modal.setAttribute('open', '');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      bindActions();
      // State-specific bindings:
      if (state === STATES.FREE_SHIP_NUDGE) bindFsnControls(data);
      return;
    }
    modal.setAttribute('data-swapping', '');
    await new Promise(r => setTimeout(r, SWAP_MS));
    body.innerHTML = '';
    while (incoming.firstChild) body.appendChild(incoming.firstChild);
    currentState = state;
    bindActions();
    if (state === STATES.FREE_SHIP_NUDGE) bindFsnControls(data);
    requestAnimationFrame(() => modal.removeAttribute('data-swapping'));
  }

  function closeModal() {
    if (!modal) return;
    modal.removeAttribute('open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    currentState = STATES.IDLE;
    body.innerHTML = '';
  }

  function bindActions() {
    if (!body) return;
    body.querySelectorAll('[data-funnel-action]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (busy) return;
        const action = btn.getAttribute('data-funnel-action');
        handleAction(action);
      }, { once: true });
    });
  }

  // ───── outcome tracking ─────
  function track(name, data) {
    try {
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, data || {}));
      if (window.gtag) window.gtag('event', name, data || {});
    } catch (e) {}
  }

  // Returns the fetch promise so callers can await it when correctness matters
  // (audit immediately before navigation). For fire-and-forget call-sites
  // (post-accept stamps that happen long before checkout), just don't await.
  // keepalive: true lets the request survive a page transition if the caller
  // doesn't await — defense in depth for the fire-and-forget pattern.
  function stampFunnelOutcome(key, value) {
    try {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [key]: value } }),
        keepalive: true
      });
    } catch (e) { return Promise.resolve(); }
  }

  function offerEq(line, key, value) {
    return !!(line.properties &&
      String(line.properties[key] || '').toLowerCase() === String(value).toLowerCase());
  }

  // ───── accepted-but-removed audit ─────
  //
  // Two design fixes baked in here:
  //
  // 1. BOGSHP Pair: R2 splits a qty=2 line into TWO qty=1 lines (paid + free
  //    split). Checking single-line quantity always fails for actual
  //    accepters. We aggregate qty per variant across non-offer lines, then
  //    compare against >= 2. Same pattern as bindCheckoutTriggers.
  //
  // 2. The other popups stamp a property (_offer=finalchance for FC1/FC2/VIP,
  //    related=true for FSN). For those we only need ANY matching line to
  //    survive — they don't have the split-line problem.
  //
  // Run from goToCheckout so every checkout transition (BOGSHP Pair decline
  // → drawer → checkout, FSN accept/decline, FC2 accept/decline, VIP
  // accept/decline) audits the same way.
  async function auditAcceptedOutcomes() {
    let cart;
    try { cart = await fetchCart(); } catch (e) { return; }
    const attrs = cart.attributes || {};

    // Pre-aggregate qty per variant for non-offer lines (used by BOGSHP Pair).
    const nonOfferQtyByVariant = new Map();
    cart.items.forEach((l) => {
      if (l.properties && l.properties._offer) return;
      const k = String(l.variant_id);
      nonOfferQtyByVariant.set(k, (nonOfferQtyByVariant.get(k) || 0) + (Number(l.quantity) || 0));
    });

    const checks = [
      // BOGSHP Pair: SUM of non-offer qty for this variant must be ≥ 2.
      // R2 splits paid + free into two qty=1 lines so we can't read a single line.
      { name: 'Funnel: BOGSHP Pair',
        survives: (v) => (nonOfferQtyByVariant.get(String(v)) || 0) >= 2 },
      // FSN options can be stamped via either tier — accept either property
      // as proof the FSN-added line survived to checkout. (related=true =
      // half_price tier, _offer=finalchance = fc_price tier.)
      { name: 'Funnel: Free Shipping Nudge (Multipack)',
        survives: (v) => cart.items.some((l) => String(l.variant_id) === String(v) &&
          (offerEq(l, 'related', 'true') || offerEq(l, '_offer', 'finalchance'))) },
      { name: 'Funnel: Free Shipping Nudge (Black)',
        survives: (v) => cart.items.some((l) => String(l.variant_id) === String(v) &&
          (offerEq(l, 'related', 'true') || offerEq(l, '_offer', 'finalchance'))) },
      // Bundle path emits TWO stamps with symmetric names — `(Bundle
      // Multipack)` for the MP variant and `(Bundle Black)` for the BK
      // variant. Reading attributes:
      //   `(Multipack)` only        → single MP add
      //   `(Black)` only            → single BK add
      //   `(Bundle Multipack)` +
      //   `(Bundle Black)`          → customer took the bundle
      // Each stamp has its own independent survives check; if one half
      // dropped post-checkout it surfaces in the audit by name.
      { name: 'Funnel: Free Shipping Nudge (Bundle Multipack)',
        survives: (v) => cart.items.some((l) => String(l.variant_id) === String(v) &&
          (offerEq(l, 'related', 'true') || offerEq(l, '_offer', 'finalchance'))) },
      { name: 'Funnel: Free Shipping Nudge (Bundle Black)',
        survives: (v) => cart.items.some((l) => String(l.variant_id) === String(v) &&
          (offerEq(l, 'related', 'true') || offerEq(l, '_offer', 'finalchance'))) },
      { name: 'Funnel: FC1 Multipack',
        survives: (v) => cart.items.some((l) => String(l.variant_id) === String(v) && offerEq(l, '_offer', 'finalchance')) },
      { name: 'Funnel: FC2 Black',
        survives: (v) => cart.items.some((l) => String(l.variant_id) === String(v) && offerEq(l, '_offer', 'finalchance')) },
      { name: 'Funnel: VIP',
        survives: (v) => cart.items.some((l) => String(l.variant_id) === String(v) && offerEq(l, '_vip', 'true')) },
    ];

    const stamps = [];
    for (const c of checks) {
      const val = attrs[c.name];
      if (!val) continue;
      const m = String(val).match(/^accepted \((\d+)\)$/);
      if (!m) continue;
      const variantId = m[1];
      if (!c.survives(variantId)) {
        // Collect each PATCH so we can await all of them before navigation.
        stamps.push(stampFunnelOutcome(c.name, `accepted but removed (${variantId})`));
      }
    }
    if (stamps.length) {
      try { await Promise.all(stamps); } catch (e) {}
    }
  }

  // Audit BEFORE navigating away. Awaited so the /cart/update.js PATCH
  // resolves before the page transitions — Shopify Flow rules don't get
  // stale `accepted (id)` values that should be `accepted but removed (id)`.
  async function goToCheckout() {
    try { await auditAcceptedOutcomes(); } catch (e) {}
    closeModal();
    window.location.href = window.Funnel.checkoutUrl || '/checkout';
  }

  // VIP last-popup hook. Audit is handled by goToCheckout's call below.
  async function finishToCheckout() {
    const vip = window.Funnel.config && window.Funnel.config.vip;
    if (!vip || !vip.enabled || !vip.variantId || window.Funnel._vipShown) {
      await goToCheckout();
      return;
    }
    let cart = null;
    try { cart = await fetchCart(); } catch (e) {}
    if (cart && cart.items.some(i => Number(i.variant_id) === Number(vip.variantId))) {
      await goToCheckout();
      return;
    }
    window.Funnel._vipShown = true;
    await setState(STATES.VIP, buildVipData());
  }

  // ───── cart-context readers (forward + reverse lookups) ─────
  function readCartBlackSibling() {
    const el = document.getElementById('funnel-cart-context');
    if (!el) return null;
    const variantId = el.dataset.siblingVariantId;
    if (!variantId) return null;
    const fcRaw = el.dataset.siblingFcPrice;
    return {
      variantId: variantId,
      title: el.dataset.siblingTitle || '',
      price: parseInt(el.dataset.siblingPrice, 10) || 0,
      image: el.dataset.siblingImage || '',
      finalChancePrice: fcRaw ? (parseInt(fcRaw, 10) || null) : null,
    };
  }

  function readMultipackSibling() {
    const el = document.getElementById('funnel-cart-context');
    if (!el) return null;
    const variantId = el.dataset.mpVariantId;
    if (!variantId) return null;
    const fcRaw = el.dataset.mpFcPrice;
    return {
      variantId: variantId,
      title: el.dataset.mpTitle || '',
      price: parseInt(el.dataset.mpPrice, 10) || 0,
      image: el.dataset.mpImage || '',
      finalChancePrice: fcRaw ? (parseInt(fcRaw, 10) || null) : null,
    };
  }

  // ───── transitions ─────
  async function handleAction(action) {
    log('action', currentState, action);
    try {
      busy = true;
      if (currentState === STATES.BOGSHP_PAIR) {
        if (action === 'accept') await onBogshpPairAccept();
        else await onBogshpPairDecline();
      } else if (currentState === STATES.FREE_SHIP_NUDGE) {
        if (action === 'accept') await onFreeShipNudgeAccept();
        else await onFreeShipNudgeDecline();
      } else if (currentState === STATES.FC1) {
        if (action === 'accept') await onFc1Accept();
        else await onFc1Decline();
      } else if (currentState === STATES.FC2) {
        if (action === 'accept') await onFc2Accept();
        else await onFc2Decline();
      } else if (currentState === STATES.VIP) {
        if (action === 'accept') await onVipAccept();
        else await onVipDecline();
      }
    } catch (err) {
      console.error('[funnel] action error:', err);
      busy = false;
      closeModal();
      dispatchCartDrawerRefresh();
      dispatchCartDrawerOpen();
      return;
    }
    busy = false;
  }

  // ───── BOGSHP_PAIR handlers ─────
  async function onBogshpPairAccept() {
    await setVariantTotalQty(product.variantId, 2);
    track('funnel_bogshp_pair_accept', { variant_id: product.variantId });
    stampFunnelOutcome('Funnel: BOGSHP Pair', `accepted (${product.variantId})`);
    closeModal();
    dispatchCartDrawerRefresh();
    dispatchCartDrawerOpen();
  }
  async function onBogshpPairDecline() {
    track('funnel_bogshp_pair_decline', { variant_id: product.variantId });
    stampFunnelOutcome('Funnel: BOGSHP Pair', 'declined');
    closeModal();
    dispatchCartDrawerRefresh();
    dispatchCartDrawerOpen();
  }

  function buildBogshpPairData() {
    const full = product.price;
    const half = Math.round(full * 0.5);
    return {
      image: product.image,
      imageAlt: product.title || '',
      'price-was': formatMoney(full),
      'price-now': formatMoney(half),
    };
  }

  // ───── FREE_SHIP_NUDGE handlers ─────
  // Local state on the popup itself — selected quantities of each option +
  // the per-SKU threshold passed through from bindCheckoutTriggers (which
  // reads it from the qualifier's shipping.free_shipping_threshold metafield
  // via funnel-cart-context). refreshFsn() reads from fsnState.thresholdCents.
  let fsnState = { multipack: 0, black: 0, bundle: 0, thresholdCents: 0, _data: null };

  // Per-option config from theme settings. Each option is independently
  // show/hide-able and can use either half-price (R3 stamp `related=true`)
  // or FC-price (R4 stamp `_offer=finalchance`, reads final_chance.price
  // metafield). Graceful hide when FC tier picked but metafield missing.
  function resolveFsnOption(kind, full, fcCents) {
    const cfg = window.Funnel.config || {};
    const showKey = kind === 'mp' ? 'fsnShowMultipack' : 'fsnShowBlack';
    const tierKey = kind === 'mp' ? 'fsnMultipackTier' : 'fsnBlackTier';
    if (cfg[showKey] === false) return null;          // hidden by merchant
    const tier = cfg[tierKey] === 'fc_price' ? 'fc_price' : 'half_price';
    if (tier === 'fc_price') {
      // FC tier requires the metafield. Without it, hide rather than
      // surprise the customer by charging full price.
      if (fcCents == null || fcCents <= 0) return null;
      return { tier: 'fc_price', priceCents: fcCents };
    }
    return { tier: 'half_price', priceCents: Math.round((Number(full) || 0) * 0.5) };
  }

  // Bundle option — independent tiers per item so the merchant can mix
  // (MP at half-price + BK at FC anchor is the strategic default).
  // If either half can't be priced at its configured tier, bundle hides.
  function resolveFsnBundleOption(kind, full, fcCents) {
    const cfg = window.Funnel.config || {};
    if (cfg.fsnShowBundle === false) return null;
    const tierKey = kind === 'mp' ? 'fsnBundleMpTier' : 'fsnBundleBkTier';
    const tier = cfg[tierKey] === 'fc_price' ? 'fc_price' : 'half_price';
    if (tier === 'fc_price') {
      if (fcCents == null || fcCents <= 0) return null;
      return { tier: 'fc_price', priceCents: fcCents };
    }
    return { tier: 'half_price', priceCents: Math.round((Number(full) || 0) * 0.5) };
  }

  function buildFreeShipNudgeData(ctx) {
    ctx = ctx || {};
    // Multipack option — ADD ANOTHER of the multipack already in cart at
    // the merchant-configured tier price. Multipack's FC price comes from
    // funnel-cart-context's data-mp-incart-fc-price emission.
    const mpLine = ctx.multipackLine || null;
    const incart = readInCartMultipack();
    const mpFull = mpLine ? mpLine.price : 0;
    const mpFc = incart ? incart.finalChancePrice : null;
    const mpOption = mpLine ? resolveFsnOption('mp', mpFull, mpFc) : null;

    // Black option — ADD a related black at the configured tier. Black's
    // FC price comes from data-sibling-fc-price (the black sibling lookup).
    const bksib = readCartBlackSibling();
    const bkFull = bksib ? bksib.price : 0;
    const bkFc = bksib ? bksib.finalChancePrice : null;
    const bkOption = bksib ? resolveFsnOption('bk', bkFull, bkFc) : null;

    // Bundle option — needs BOTH sides to resolve at their independent
    // bundle tiers. If either side fails (metafield missing for FC tier,
    // or no sibling found at all), the entire bundle is hidden.
    const bundleMp = mpLine ? resolveFsnBundleOption('mp', mpFull, mpFc) : null;
    const bundleBk = bksib ? resolveFsnBundleOption('bk', bkFull, bkFc) : null;
    const bundle = (bundleMp && bundleBk) ? {
      mpVariantId: mpLine.variant_id,
      bkVariantId: bksib.variantId,
      mpTier: bundleMp.tier,
      bkTier: bundleBk.tier,
      mpPriceCents: bundleMp.priceCents,
      bkPriceCents: bundleBk.priceCents,
      priceCents: bundleMp.priceCents + bundleBk.priceCents,   // bundle "now" sum
      fullCents: mpFull + bkFull,                              // bundle "was" sum
      mpImage: mpLine.image || mpLine.featured_image || '',
      bkImage: bksib.image || '',
    } : null;

    fsnState = {
      multipack: 0,
      black: 0,
      bundle: 0,
      thresholdCents: Number(ctx.thresholdCents) || 0,
      _data: {
        mp: (mpLine && mpOption) ? {
          variantId: mpLine.variant_id,
          title: mpLine.product_title || mpLine.title || 'Multipack',
          image: mpLine.image || mpLine.featured_image || '',
          fullCents: mpFull,
          priceCents: mpOption.priceCents,
          tier: mpOption.tier,
        } : null,
        bk: (bksib && bkOption) ? {
          variantId: bksib.variantId,
          title: bksib.title || 'Black',
          image: bksib.image || '',
          fullCents: bkFull,
          priceCents: bkOption.priceCents,
          tier: bkOption.tier,
        } : null,
        bundle: bundle,
      },
    };
    // Pre-seed FSN base cents so refreshFsn() renders immediately with a
    // known cart total instead of waiting for a follow-up fetch.
    if (typeof ctx.cartTotal === 'number') window.Funnel._fsnBaseCents = ctx.cartTotal;
    return {
      'opt-mp-image': fsnState._data.mp ? fsnState._data.mp.image : '',
      'opt-mp-was': fsnState._data.mp ? formatMoney(fsnState._data.mp.fullCents) : '',
      'opt-mp-now': fsnState._data.mp ? formatMoney(fsnState._data.mp.priceCents) : '',
      'opt-bk-image': fsnState._data.bk ? fsnState._data.bk.image : '',
      'opt-bk-was': fsnState._data.bk ? formatMoney(fsnState._data.bk.fullCents) : '',
      'opt-bk-now': fsnState._data.bk ? formatMoney(fsnState._data.bk.priceCents) : '',
      'opt-bundle-mp-image': fsnState._data.bundle ? fsnState._data.bundle.mpImage : '',
      'opt-bundle-bk-image': fsnState._data.bundle ? fsnState._data.bundle.bkImage : '',
      'opt-bundle-was': fsnState._data.bundle ? formatMoney(fsnState._data.bundle.fullCents) : '',
      'opt-bundle-now': fsnState._data.bundle ? formatMoney(fsnState._data.bundle.priceCents) : '',
    };
  }

  // Returns true if any FSN option is actually renderable. Called by the
  // checkout-click branch to decide whether to fire the popup at all.
  function fsnHasAnyOption() {
    return !!(
      (fsnState._data && fsnState._data.mp) ||
      (fsnState._data && fsnState._data.bk) ||
      (fsnState._data && fsnState._data.bundle)
    );
  }

  // Render the "+ Add" pill OR a stepper into the option control slot.
  function renderFsnControl(which) {
    if (!body) return;
    const slot = body.querySelector(`[data-fn-control="${which}"]`);
    if (!slot) return;
    const qty = fsnState[which];
    if (qty <= 0) {
      slot.innerHTML = `<button type="button" class="fn__opt-add" data-fn-add="${which}">+ ${escHtml((window.Funnel.config && window.Funnel.config.fsnAddLabel) || 'Add')}</button>`;
      slot.querySelector('[data-fn-add]').addEventListener('click', () => {
        fsnState[which] = 1;
        refreshFsn();
      });
    } else {
      slot.innerHTML = `
        <div class="fn__stepper">
          <button type="button" class="fn__stepper-btn" data-fn-step="${which}:-1" aria-label="-">&minus;</button>
          <span class="fn__stepper-value">${qty}</span>
          <button type="button" class="fn__stepper-btn" data-fn-step="${which}:+1" aria-label="+">+</button>
        </div>`;
      slot.querySelectorAll('[data-fn-step]').forEach(b => {
        b.addEventListener('click', () => {
          const [k, delta] = b.dataset.fnStep.split(':');
          const next = Math.max(0, Math.min(3, fsnState[k] + parseInt(delta, 10)));
          fsnState[k] = next;
          refreshFsn();
        });
      });
    }
    const row = body.querySelector(`[data-fn-opt="${which}"]`);
    if (row) row.classList.toggle('fn__opt--on', qty > 0);
  }

  function refreshFsn() {
    if (!body || !fsnState._data) return;
    const cfg = window.Funnel.config || {};
    const baseCents = (window.Funnel._fsnBaseCents != null) ? window.Funnel._fsnBaseCents : 0;
    const mpAdd = (fsnState._data.mp ? fsnState._data.mp.priceCents : 0) * fsnState.multipack;
    const bkAdd = (fsnState._data.bk ? fsnState._data.bk.priceCents : 0) * fsnState.black;
    const bdAdd = (fsnState._data.bundle ? fsnState._data.bundle.priceCents : 0) * fsnState.bundle;
    const total = baseCents + mpAdd + bkAdd + bdAdd;
    // Per-SKU threshold from the qualifier's metafield, captured at
    // buildFreeShipNudgeData. Guard: if 0 the popup shouldn't have opened
    // (bindCheckoutTriggers gates on > 0), but defend against a 0-divide.
    const thresholdCents = fsnState.thresholdCents || 1;
    const remaining = Math.max(0, thresholdCents - total);
    const unlocked = remaining <= 0;
    const pct = Math.min(100, Math.round((total / thresholdCents) * 100));

    const bar = body.querySelector('[data-fn-bar]');
    if (bar) bar.classList.toggle('fn__bar--on', unlocked);
    const fill = body.querySelector('[data-fn-bar-fill]');
    if (fill) fill.style.width = pct + '%';
    const barText = body.querySelector('[data-slot="bar-text"]');
    if (barText) {
      const t = unlocked ? (cfg.fsnBarUnlocked || 'Free shipping unlocked')
                         : (cfg.fsnBarLocked || 'Add {remaining} more to get free shipping').replace('{remaining}', formatMoney(remaining));
      barText.innerHTML = t.replace(/<strong>(.*?)<\/strong>/g, '<strong>$1</strong>');
    }
    const titleDyn = body.querySelector('[data-slot="title-dyn"]');
    if (titleDyn) {
      titleDyn.textContent = unlocked
        ? (cfg.fsnTitleUnlocked || 'Free shipping unlocked')
        : (cfg.fsnTitleLocked || "You're {remaining} from free shipping").replace('{remaining}', formatMoney(remaining));
    }
    // Footer breakdown — Items / Shipping (with strikethrough on unlock) / Order total.
    // Shipping rate is cosmetic — actual charge comes from Shopify zones at checkout.
    // The strikethrough+FREE framing makes the saving tangible at decision time.
    const shippingCents = Number(cfg.fsnShippingRateCents) || 0;
    const itemsTotalEl = body.querySelector('[data-slot="items-total"]');
    if (itemsTotalEl) itemsTotalEl.textContent = formatMoney(total);

    const shipWasEl = body.querySelector('[data-slot="shipping-was"]');
    const shipNowEl = body.querySelector('[data-slot="shipping-now"]');
    const shipRow = body.querySelector('[data-fn-shipping-row]');
    if (shipRow) shipRow.classList.toggle('fn__row--unlocked', unlocked);
    if (shipWasEl && shipNowEl) {
      if (unlocked) {
        shipWasEl.textContent = formatMoney(shippingCents);
        shipNowEl.textContent = cfg.fsnShippingFreeLabel || 'FREE';
      } else {
        shipWasEl.textContent = '';
        shipNowEl.textContent = formatMoney(shippingCents);
      }
    }

    const totalEl = body.querySelector('[data-slot="total"]');
    if (totalEl) {
      // Order total = items + (unlocked ? 0 : shipping).
      const orderTotal = total + (unlocked ? 0 : shippingCents);
      totalEl.textContent = formatMoney(orderTotal);
    }
    const ctaText = body.querySelector('[data-slot="cta-text"]');
    if (ctaText) ctaText.textContent = unlocked ? (cfg.fsnCtaUnlocked || 'Checkout with free shipping')
                                                : (cfg.fsnCtaLocked || 'Continue to checkout');

    renderFsnControl('multipack');
    renderFsnControl('black');
    renderFsnControl('bundle');
  }

  async function bindFsnControls(data) {
    // If buildFreeShipNudgeData pre-seeded the base, keep it. Otherwise
    // fetch the cart once to set the base before first render.
    if (typeof window.Funnel._fsnBaseCents !== 'number') {
      try {
        const cart = await fetchCart();
        window.Funnel._fsnBaseCents = cart.total_price || 0;
      } catch (e) { window.Funnel._fsnBaseCents = 0; }
    }
    // Single-option layout — hide the entire row of any option that's null
    // (merchant disabled OR FC tier picked but no metafield set on the
    // product). Doing it in JS rather than CSS keeps the layout calculation
    // straightforward (display:none collapses the row + sibling gap).
    if (body) {
      const mpRow = body.querySelector('[data-fn-opt="multipack"]');
      const bkRow = body.querySelector('[data-fn-opt="black"]');
      const bdRow = body.querySelector('[data-fn-opt="bundle"]');
      if (mpRow) mpRow.style.display = (fsnState._data && fsnState._data.mp) ? '' : 'none';
      if (bkRow) bkRow.style.display = (fsnState._data && fsnState._data.bk) ? '' : 'none';
      if (bdRow) bdRow.style.display = (fsnState._data && fsnState._data.bundle) ? '' : 'none';
    }
    refreshFsn();
  }

  // Translates a tier to the cart-line property the discount engine consumes.
  //   half_price → { related: 'true' }     → R3 50% off (anchor gate enforced)
  //   fc_price   → { _offer: 'finalchance' } → R4 reprice to final_chance.price
  function propsForTier(tier) {
    return tier === 'fc_price'
      ? { '_offer': 'finalchance' }
      : { 'related': 'true' };
  }

  async function onFreeShipNudgeAccept() {
    const d = fsnState._data || {};
    // FSN→checkout has ZERO buffer between accept and navigation, so the
    // fire-and-forget `keepalive` pattern intermittently loses the stamps
    // when the browser aborts in-flight requests on `location.href = …`.
    // (Confirmed on inkjet.de 12 Jun 2026: order #124618 lost FSN stamp,
    // sibling order #124619 captured it.) Collect stamps as promises +
    // Promise.allSettled before navigating, so the writes are guaranteed
    // to land. Parallel execution keeps the added latency to one RTT.
    const stamps = [];
    // Per-option tier picked by merchant in customizer. Properties stamped
    // accordingly so the right discount rule (R3 or R4) reprices the line.
    if (fsnState.multipack > 0 && d.mp && d.mp.variantId) {
      await cartAdd(d.mp.variantId, fsnState.multipack, propsForTier(d.mp.tier));
      stamps.push(stampFunnelOutcome('Funnel: Free Shipping Nudge (Multipack)', `accepted (${d.mp.variantId})`));
      track('funnel_fsn_accept_mp', { variant_id: d.mp.variantId, quantity: fsnState.multipack, tier: d.mp.tier });
    }
    if (fsnState.black > 0 && d.bk && d.bk.variantId) {
      await cartAdd(d.bk.variantId, fsnState.black, propsForTier(d.bk.tier));
      stamps.push(stampFunnelOutcome('Funnel: Free Shipping Nudge (Black)', `accepted (${d.bk.variantId})`));
      track('funnel_fsn_accept_bk', { variant_id: d.bk.variantId, quantity: fsnState.black, tier: d.bk.tier });
    }
    // Bundle accept = TWO cart writes (1 MP + 1 BK × bundle qty), each at
    // its own independent tier. The two added lines carry different
    // properties so R3 + R4 fire on the right ones at checkout. Stamps
    // emit in the standard `accepted (variant_id)` format with symmetric
    // names — `(Bundle Multipack)` for the MP, `(Bundle Black)` for the BK.
    if (fsnState.bundle > 0 && d.bundle && d.bundle.mpVariantId && d.bundle.bkVariantId) {
      await cartAdd(d.bundle.mpVariantId, fsnState.bundle, propsForTier(d.bundle.mpTier));
      await cartAdd(d.bundle.bkVariantId, fsnState.bundle, propsForTier(d.bundle.bkTier));
      stamps.push(stampFunnelOutcome('Funnel: Free Shipping Nudge (Bundle Multipack)', `accepted (${d.bundle.mpVariantId})`));
      stamps.push(stampFunnelOutcome('Funnel: Free Shipping Nudge (Bundle Black)',     `accepted (${d.bundle.bkVariantId})`));
      track('funnel_fsn_accept_bundle', {
        mp_variant_id: d.bundle.mpVariantId,
        bk_variant_id: d.bundle.bkVariantId,
        quantity: fsnState.bundle,
        mp_tier: d.bundle.mpTier,
        bk_tier: d.bundle.bkTier,
      });
    }
    if (stamps.length === 0) {
      stamps.push(stampFunnelOutcome('Funnel: Free Shipping Nudge', 'declined'));
    }
    // Wait for all writes to land before navigating. allSettled (not all)
    // so one failing stamp doesn't block the others — never let a stamp
    // failure trap the customer in the popup. Typical latency: ~150-300ms
    // for the slowest single write since they run in parallel.
    await Promise.allSettled(stamps);
    goToCheckout();
  }
  async function onFreeShipNudgeDecline() {
    // Same race-condition guard as accept. Single decline stamp, awaited
    // before navigation.
    if (fsnState.multipack === 0 && fsnState.black === 0 && fsnState.bundle === 0) {
      await Promise.allSettled([
        stampFunnelOutcome('Funnel: Free Shipping Nudge', 'declined'),
      ]);
    }
    goToCheckout();
  }

  // ───── FC1 + FC2 (decliner sequential rescue) ─────
  // Cache the multipack target across FC1 → FC2 so onFc1Accept can still
  // identify which variant to add even when the customer isn't on a PDP.
  let fcMultipackTarget = null;

  // Reads in-cart multipack FC data emitted by funnel-cart-context.liquid.
  // cart.js can't see product metafields → Liquid emits them as data-* and
  // we read them here so the FC1 display matches the engine's R4 reprice.
  function readInCartMultipack() {
    const el = document.getElementById('funnel-cart-context');
    if (!el) return null;
    const variantId = el.dataset.mpIncartVariantId;
    if (!variantId) return null;
    const fcRaw = el.dataset.mpIncartFcPrice;
    const fstRaw = el.dataset.mpIncartFreeShippingThreshold;
    return {
      variantId: variantId,
      title: el.dataset.mpIncartTitle || '',
      price: parseInt(el.dataset.mpIncartPrice, 10) || 0,
      image: el.dataset.mpIncartImage || '',
      finalChancePrice: fcRaw ? (parseInt(fcRaw, 10) || null) : null,
      // Per-SKU free-shipping threshold (cents). Null if metafield unset
      // on this product — that case suppresses the FSN popup entirely.
      freeShippingThresholdCents: fstRaw ? (parseInt(fstRaw, 10) || null) : null,
    };
  }

  function buildFc1Data(ctx) {
    ctx = ctx || {};
    // FC1 = multipack at final_chance.price. Source order:
    //   1. funnel-cart-context's data-mp-incart-* (Liquid-emitted, includes
    //      the metafield → matches what R4 will actually charge)
    //   2. ctx.multipackLine (cart drawer flow — line we already matched,
    //      but no metafield access from cart.js)
    //   3. window.Funnel.product (multipack PDP flow)
    let variantId = null, fullCents = 0, fcCents = null, image = '', title = '';
    const incart = readInCartMultipack();
    if (incart && incart.variantId) {
      variantId = incart.variantId;
      fullCents = incart.price;
      fcCents = incart.finalChancePrice; // real metafield value when set
      image = incart.image;
      title = incart.title;
    } else if (ctx.multipackLine) {
      variantId = ctx.multipackLine.variant_id;
      fullCents = ctx.multipackLine.price;
      fcCents = null; // no metafield available without Liquid emit
      image = ctx.multipackLine.image || ctx.multipackLine.featured_image || '';
      title = ctx.multipackLine.product_title || ctx.multipackLine.title || '';
    } else if (product) {
      variantId = product.variantId;
      fullCents = product.price;
      fcCents = product.finalChancePrice;
      image = product.image;
      title = product.title || '';
    }
    fcMultipackTarget = { variantId: variantId, fcCents: fcCents };
    const displayCents = fcCents != null ? fcCents : Math.round(fullCents * 0.4);
    return {
      image: image,
      imageAlt: title,
      'price-was': formatMoney(fullCents),
      'price-now': formatMoney(displayCents),
    };
  }
  async function onFc1Accept() {
    const target = fcMultipackTarget;
    if (!target || !target.variantId) { await setState(STATES.FC2, buildFc2Data()); return; }
    await cartAdd(target.variantId, 1, { '_offer': 'finalchance' });
    track('funnel_fc1_accept', { variant_id: target.variantId });
    stampFunnelOutcome('Funnel: FC1 Multipack', `accepted (${target.variantId})`);
    await setState(STATES.FC2, buildFc2Data());
  }
  async function onFc1Decline() {
    const vid = (fcMultipackTarget && fcMultipackTarget.variantId) || (product && product.variantId) || '';
    track('funnel_fc1_decline', { variant_id: vid });
    stampFunnelOutcome('Funnel: FC1 Multipack', 'declined');
    await setState(STATES.FC2, buildFc2Data());
  }

  function buildFc2Data() {
    // FC2 = black at final_chance.price. Read black sibling from cart context.
    const bksib = readCartBlackSibling();
    if (!bksib) return { 'price-was': '', 'price-now': '', image: '' };
    const full = bksib.price;
    const fc = bksib.finalChancePrice || Math.round(full * 0.33);
    return {
      image: bksib.image,
      imageAlt: bksib.title || '',
      'price-was': formatMoney(full),
      'price-now': formatMoney(fc),
    };
  }
  async function onFc2Accept() {
    const bksib = readCartBlackSibling();
    if (!bksib || !bksib.variantId) { goToCheckout(); return; }
    await cartAdd(bksib.variantId, 1, { '_offer': 'finalchance' });
    track('funnel_fc2_accept', { variant_id: bksib.variantId });
    stampFunnelOutcome('Funnel: FC2 Black', `accepted (${bksib.variantId})`);
    finishToCheckout();
  }
  async function onFc2Decline() {
    track('funnel_fc2_decline');
    stampFunnelOutcome('Funnel: FC2 Black', 'declined');
    finishToCheckout();
  }

  // ───── VIP (legacy, toggle-off by default) ─────
  function buildVipData() {
    const vip = (window.Funnel.config && window.Funnel.config.vip) || {};
    const full = vip.price || 0;
    const fc = vip.finalChancePrice || full;
    return {
      image: vip.image || '',
      imageAlt: vip.title || '',
      'price-was': full > fc ? formatMoney(full) : '',
      'price-now': formatMoney(fc),
    };
  }
  async function onVipAccept() {
    const vip = window.Funnel.config && window.Funnel.config.vip;
    if (!vip || !vip.variantId) { goToCheckout(); return; }
    await cartAdd(vip.variantId, 1, { '_offer': 'finalchance', '_vip': 'true' });
    track('funnel_vip_accept', { variant_id: vip.variantId });
    stampFunnelOutcome('Funnel: VIP', `accepted (${vip.variantId})`);
    goToCheckout();
  }
  async function onVipDecline() {
    track('funnel_vip_decline');
    stampFunnelOutcome('Funnel: VIP', 'declined');
    goToCheckout();
  }

  // ───── gates ─────
  // BOGSHP_PAIR runs at ATC for BOGSHP-tagged products. Skip if a non-FC
  // line of this variant is already at qty ≥ 2 (returning-customer guard).
  async function canShowBogshpPair() {
    if (!(product && Array.isArray(product.tags) && product.tags.indexOf('BOGSHP') >= 0)) return false;
    try {
      const cart = await fetchCart();
      const existing = cart.items.find(i =>
        Number(i.variant_id) === Number(product.variantId) &&
        !(i.properties && i.properties._offer)
      );
      if (existing && existing.quantity >= 2) return false;
    } catch (e) {}
    return true;
  }

  // ───── ATC handling ─────
  async function handleAtcClick(triggerEl) {
    if (!product || !product.variantId) {
      log('no product context — falling back to native form submit');
      const form = triggerEl.closest('form');
      if (form) form.submit();
      return;
    }
    let cart;
    try { cart = await fetchCart(); } catch (e) { log('cart read failed'); }
    const already = cart ? cart.items.find(i =>
      Number(i.variant_id) === Number(product.variantId) &&
      !(i.properties && i.properties._offer)
    ) : null;
    if (!already) {
      try {
        await cartAdd(product.variantId, 1);
        track('funnel_atc', { variant_id: product.variantId });
        dispatchCartDrawerRefresh();
      } catch (e) {
        console.error('[funnel] ATC failed:', e);
        return;
      }
    }
    if (await canShowBogshpPair()) {
      await setState(STATES.BOGSHP_PAIR, buildBogshpPairData());
    } else {
      dispatchCartDrawerOpen();
    }
  }

  // ───── checkout-click orchestrator ─────
  // Branches based on whether the current cart shows a BOGSHP accepter
  // (multipack qty≥2 with no _offer) vs a BOGSHP rejecter (multipack qty=1).
  function bindCheckoutTriggers() {
    document.addEventListener('click', async function (event) {
      const trigger = event.target.closest('[data-funnel-checkout]');
      if (!trigger) return;
      event.preventDefault();

      // Debounce same-drawer re-clicks.
      if (window.Funnel._chainFiredAt && (Date.now() - window.Funnel._chainFiredAt < 60_000)) {
        finishToCheckout();
        return;
      }
      window.Funnel._chainFiredAt = Date.now();

      let cart;
      try { cart = await fetchCart(); } catch (e) { finishToCheckout(); return; }

      // SOURCE OF TRUTH for "is there a BOGSHP qualifier in cart?" is
      // funnel-cart-context.liquid's forward lookup (Liquid sees product
      // tags; cart.js does NOT include product_tags so a client-side tag
      // check returns false for every line). We read the emitted in-cart
      // variant ID and match cart lines against it. R2 may split qty=2
      // into paid + free lines (same variant, different cart entries) so
      // we sum qty across ALL non-offer matches for that variant.
      const incartCtx = readInCartMultipack();
      let mpLine = null, mpTotalQty = 0;
      if (incartCtx && incartCtx.variantId) {
        const qualifierLines = cart.items.filter(i =>
          Number(i.variant_id) === Number(incartCtx.variantId) &&
          !(i.properties && i.properties._offer)
        );
        qualifierLines.forEach(l => {
          mpTotalQty += Number(l.quantity) || 0;
          // Pick the highest-priced (paid) line as visual representative
          // — protects against picking the €0 free-split line for image/title.
          if (!mpLine || (Number(l.price) || 0) > (Number(mpLine.price) || 0)) mpLine = l;
        });
      }

      // BOGSHP accepter = aggregated qty ≥ 2. Rejecter = qty 1.
      const isAccepter = mpTotalQty >= 2;
      const isRejecter = mpTotalQty === 1;

      // Close cart drawer first so the popup owns the screen.
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close', { bubbles: true }));

      if (isAccepter) {
        // Per-SKU free-shipping threshold from the qualifier's
        // shipping.free_shipping_threshold metafield (emitted via
        // funnel-cart-context). Same source the inkfactory-shipping Function
        // consumes server-side at checkout, so popup-vs-engine drift is
        // impossible. Suppress the popup if:
        //   - no metafield value (product not enrolled in the rule), OR
        //   - cart already over threshold (no nudge needed; topper unlocked).
        const thresholdCents = incartCtx && incartCtx.freeShippingThresholdCents;
        if (thresholdCents && cart.total_price < thresholdCents) {
          // Refresh the live cheapest shipping rate from Shopify zones
          // before rendering so the strike-through figure matches reality.
          // Bounded to ~500ms (one HTTP call) + cached for 1h via session.
          // Failure mode = silent fallback to the static fsnShippingRateCents.
          try {
            const liveRate = await fetchCheapestShippingRateCents();
            if (liveRate != null) {
              window.Funnel.config.fsnShippingRateCents = liveRate;
            }
          } catch (e) {}
          // Reset cached base so buildFreeShipNudgeData's pre-seed lands.
          window.Funnel._fsnBaseCents = undefined;
          const fsnData = buildFreeShipNudgeData({
            multipackLine: mpLine,
            cartTotal: cart.total_price,
            thresholdCents: thresholdCents,
          });
          // Per-option config may have hidden BOTH options (merchant has
          // disabled both, or both are on FC tier without metafields set).
          // No options to render → skip the popup entirely, straight to
          // checkout. Prevents an empty popup with only the CTA + Skip.
          if (!fsnHasAnyOption()) {
            finishToCheckout();
            return;
          }
          await setState(STATES.FREE_SHIP_NUDGE, fsnData);
        } else {
          finishToCheckout();
        }
        return;
      }
      if (isRejecter) {
        // FC1 needs the multipack line for image+price (no product context off-PDP).
        await setState(STATES.FC1, buildFc1Data({ multipackLine: mpLine }));
        return;
      }
      // No multipack in cart (e.g. black-only PDP customer) → straight to checkout.
      finishToCheckout();
    });
  }

  function bindAtcTriggers() {
    document.addEventListener('click', function (event) {
      const trigger = event.target.closest('[data-funnel-atc]');
      if (!trigger) return;
      event.preventDefault();
      handleAtcClick(trigger);
    });
  }

  // ───── modal dismissal (backdrop + ESC) ─────
  function bindModalDismissals() {
    if (!modal) return;
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        if (busy) return;
        handleAction('decline');
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (currentState === STATES.IDLE) return;
      if (busy) return;
      handleAction('decline');
    });
  }

  // ───── boot ─────
  bindAtcTriggers();
  bindCheckoutTriggers();
  bindModalDismissals();

  // ───── helpers ─────
  function escHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ───── exports for debugging ─────
  window.Funnel.STATES = STATES;
  window.Funnel.setState = setState;
  window.Funnel.handleAtcClick = handleAtcClick;
})();
